import React, { useContext, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Devices from './Devices';
import Scanner from './Scanner';
import Users from './Users';
import CardTemplate from '../../shared/CardTemplate';
import { helpDeskCardId, subTabsPaths } from '../../../constants';
import { AppStateContext } from '../../../context/AppContext';
import { TabsStateContext, TabsDispatchContext } from '../../../context/TabsContext';
import {
    helpdeskAllowedPrivileges,
    isAllowed,
} from '../../../utils/authorization';
import {
    StyledAlert,
    PortalContainer,
    AccentButton,
} from '../../../styles/common';

const ComponentsList = ({ tab }) => {
    switch (tab) {
        case subTabsPaths.helpDesk.users:
            return <Users />;
        case subTabsPaths.helpDesk.devices:
            return <Devices />;
        case subTabsPaths.helpDesk.scanner:
            return <Scanner />;
        default:
            return null;
    }
};

const HelpDesk = () => {
    const { user, ucmsTokenAvailable } = useContext(AppStateContext);
    const { activeIndex, tabs } = useContext(TabsStateContext);
    const dispatch = useContext(TabsDispatchContext);
    const location = useLocation();
    const { t } = useTranslation();

    const { title, subtitle, route } = tabs[activeIndex] || {};

    useEffect(() => {
        if (tabs.length > 0) {
            const index = tabs.findIndex((item) =>
                location.pathname.includes(item.route),
            );
            if(index > 0) dispatch({ type: 'SET_ACTIVE_INDEX', payload: index });

        }
    }, [location, tabs]);

    // Show loading state while checking token
    if (ucmsTokenAvailable === null) {
        return (
            <CardTemplate
                title={t(title)}
                subtitle={t(subtitle)}
                testId={helpDeskCardId}
            >
                <PortalContainer>
                    <div>{t('helpDesk$checkingAvailability')}</div>
                </PortalContainer>
            </CardTemplate>
        );
    }

    // Show warning if UCMS token is unavailable
    if (ucmsTokenAvailable === false) {
        return (
            <CardTemplate
                title={t(title)}
                subtitle={t(subtitle)}
                testId={helpDeskCardId}
            >
                <PortalContainer>
                    <StyledAlert
                        id="ucms-token-unavailable-alert"
                        color="warning"
                        fade={false}
                    >
                        {t('helpDesk$ucmsTokenUnavailable')}
                    </StyledAlert>
                </PortalContainer>
            </CardTemplate>
        );
    }

    if (
        user &&
        user.privileges &&
        !isAllowed({
            allowedPermissions: helpdeskAllowedPrivileges,
            permissions: user.privileges,
        })
    ) {
        return (
            <CardTemplate
                title={t(title)}
                subtitle={t(subtitle)}
                testId={helpDeskCardId}
            >
                <PortalContainer>
                    <StyledAlert
                        id="invalid-assurance-alert"
                        color="warning"
                        fade={false}
                    >
                        {t('notAuthorized$higherAssuranceNecessary')}
                    </StyledAlert>
                    <a href={`https://${window.location.host}/operator`}>
                        <AccentButton outline color="primary" size="sm">
                            {t('notAuthorized$reloginBtn')}
                        </AccentButton>
                    </a>
                </PortalContainer>
            </CardTemplate>
        );
    }

    return (
        <CardTemplate
            title={t(title)}
            subtitle={t(subtitle)}
            testId={helpDeskCardId}
        >
            <ComponentsList tab={route} />
        </CardTemplate>
    );
};

export default HelpDesk;
